import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

import {
  decimalLatex,
  isValueCorrect,
  scoreParts,
  setField,
  toLatexExpression,
} from "./conditionalProbabilityUtils";

const TYPE = "MaturaConditionalCommunityOpenTask";

const LANGUAGES = ["niemieckim", "angielskim", "francuskim"];

const CONFIGS = [
  { educated: 0.35, langEducated: 0.7, langNotEducated: 0.4 },
  { educated: 0.3, langEducated: 0.75, langNotEducated: 0.35 },
  { educated: 0.4, langEducated: 0.65, langNotEducated: 0.25 },
  { educated: 0.45, langEducated: 0.8, langNotEducated: 0.3 },
  { educated: 0.25, langEducated: 0.6, langNotEducated: 0.2 },
];

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function percentText(value) {
  return `${Math.round(value * 100)}%`;
}

function decimalText(value, digits = 4) {
  return String(Number(value.toFixed(digits))).replace(".", ",");
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function makeTask(config = rand(CONFIGS)) {
  const language = rand(LANGUAGES);
  const intersection = config.educated * config.langEducated;
  const condition =
    config.educated * config.langEducated +
    (1 - config.educated) * config.langNotEducated;
  const probability = intersection / condition;
  const rounded = round2(probability);

  return {
    type: TYPE,
    points: 3,
    language,
    ...config,
    condition,
    intersection,
    probability,
    rounded,
  };
}

export function generateTask() {
  return makeTask();
}

export function renderTask(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Zadanie maturalne - otwarte (0-3 pkt).</strong>
      </p>
      <p>
        W pewnej lokalnej społeczności {percentText(task.educated)} osób ma
        wyższe wykształcenie. W tej społeczności językiem {task.language} dobrze
        włada {percentText(task.langEducated)} osób mających wyższe wykształcenie
        i {percentText(task.langNotEducated)} osób bez wyższego wykształcenia.
        Spośród członków tej społeczności wybieramy losowo jedną osobę.
      </p>
      <p>
        <strong>
          Oblicz prawdopodobieństwo zdarzenia polegającego na tym, że wybierzemy
          osobę z wyższym wykształceniem, jeśli wiadomo, że ta osoba dobrze włada
          językiem {task.language}. Wynik zapisz w postaci ułamka dziesiętnego w
          zaokrągleniu do części setnych. Zapisz obliczenia.
        </strong>
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  return (
    <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p>
          <strong>Krok 1 (1 pkt).</strong> Oblicz prawdopodobieństwo zdarzenia{" "}
          <InlineMath math="B" />: wybrana osoba dobrze włada językiem{" "}
          {task.language}.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math="P(B) =" />
          <EquationInput
            value={userAnswer.condition || ""}
            onChange={(v) => setField(userAnswer, "condition", v, onChange)}
            disabled={disabled}
            placeholder="np. 0,35*0,70+0,65*0,40"
            showPreview={true}
            basis="360px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 2 (1 pkt).</strong> Oblicz prawdopodobieństwo zdarzenia{" "}
          <InlineMath math={"A\\cap B"} />: wybrana osoba ma wyższe wykształcenie
          i dobrze włada językiem {task.language}.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math={"P(A\\cap B) ="} />
          <EquationInput
            value={userAnswer.intersection || ""}
            onChange={(v) => setField(userAnswer, "intersection", v, onChange)}
            disabled={disabled}
            placeholder="np. 0,35*0,70"
            showPreview={true}
            basis="260px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 3 (1 pkt).</strong> Oblicz prawdopodobieństwo warunkowe po
          zaokrągleniu do części setnych:
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math="P(A|B) =" />
          <EquationInput
            value={userAnswer.result || ""}
            onChange={(v) => setField(userAnswer, "result", v, onChange)}
            disabled={disabled}
            placeholder="np. 0,49"
            showPreview={true}
            basis="220px"
          />
        </div>
      </div>
    </div>
  );
}

export function checkAnswer(task, userAnswer = {}) {
  return scoreParts([
    {
      key: "condition",
      label: "prawdopodobieństwo P(B)",
      correct: isValueCorrect(userAnswer.condition, task.condition, 1e-9),
    },
    {
      key: "intersection",
      label: "prawdopodobieństwo P(A∩B)",
      correct: isValueCorrect(userAnswer.intersection, task.intersection, 1e-9),
    },
    {
      key: "result",
      label: "prawdopodobieństwo warunkowe",
      correct:
        isValueCorrect(userAnswer.result, task.rounded, 0.005) ||
        isValueCorrect(userAnswer.result, task.probability, 0.0005),
    },
  ]);
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div className="space-y-1">
      <p>
        <InlineMath math={`P(B)=${toLatexExpression(userAnswer.condition)}`} />
      </p>
      <p>
        <InlineMath math={`P(A\\cap B)=${toLatexExpression(userAnswer.intersection)}`} />
      </p>
      <p>
        <InlineMath math={`P(A|B)=${toLatexExpression(userAnswer.result)}`} />
      </p>
    </div>
  );
}

export function renderAnswer(task) {
  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong>{" "}
        <InlineMath
          math={`P(B)=${decimalLatex(task.educated)}\\cdot ${decimalLatex(task.langEducated)}+${decimalLatex(
            1 - task.educated
          )}\\cdot ${decimalLatex(task.langNotEducated)}=${decimalLatex(decimalText(task.condition))}`}
        />
      </p>
      <p>
        <strong>Krok 2:</strong>{" "}
        <InlineMath
          math={`P(A\\cap B)=${decimalLatex(task.educated)}\\cdot ${decimalLatex(
            task.langEducated
          )}=${decimalLatex(decimalText(task.intersection))}`}
        />
      </p>
      <p>
        <strong>Krok 3:</strong>{" "}
        <InlineMath
          math={`P(A|B)=\\frac{P(A\\cap B)}{P(B)}\\approx ${decimalLatex(
            decimalText(task.rounded, 2)
          )}`}
        />
      </p>
    </div>
  );
}

export function renderExplanation(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Krok 1.</strong> Niech <InlineMath math="A" /> oznacza zdarzenie,
        że wybrana osoba ma wyższe wykształcenie, a <InlineMath math="B" /> -
        że dobrze włada językiem {task.language}. Prawdopodobieństwo zdarzenia{" "}
        <InlineMath math="B" /> liczymy z dwóch rozłącznych przypadków.
      </p>
      <BlockMath
        math={`P(B)=${decimalLatex(task.educated)}\\cdot ${decimalLatex(task.langEducated)}+${decimalLatex(
          1 - task.educated
        )}\\cdot ${decimalLatex(task.langNotEducated)}=${decimalLatex(decimalText(task.condition))}`}
      />

      <p>
        <strong>Krok 2.</strong> Zdarzenie <InlineMath math={"A\\cap B"} /> oznacza,
        że osoba jednocześnie ma wyższe wykształcenie i dobrze włada wskazanym
        językiem.
      </p>
      <BlockMath
        math={`P(A\\cap B)=${decimalLatex(task.educated)}\\cdot ${decimalLatex(
          task.langEducated
        )}=${decimalLatex(decimalText(task.intersection))}`}
      />

      <p>
        <strong>Krok 3.</strong> Podstawiamy do wzoru na prawdopodobieństwo
        warunkowe i zaokrąglamy wynik do części setnych.
      </p>
      <BlockMath
        math={`P(A|B)=\\frac{P(A\\cap B)}{P(B)}=\\frac{${decimalLatex(
          decimalText(task.intersection)
        )}}{${decimalLatex(decimalText(task.condition))}}\\approx ${decimalLatex(
          decimalText(task.rounded, 2)
        )}`}
      />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
